import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { ArrowLeft, Clock, Tag, AlertTriangle, Headset } from "lucide-react";
import { toast } from "sonner";

const statusColor = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

const STATUSES = ["open", "in_progress", "resolved", "closed"];

export default function TicketDetailPage() {
  const { id } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/tickets/${id}`).then((r) => setTicket(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const updateStatus = async (status) => {
    try {
      const { data } = await api.patch(`/tickets/${id}/status`, { status });
      setTicket(data);
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  if (loading) return <div className="p-12 text-zinc-400 text-sm">Loading ticket…</div>;
  if (!ticket) return <div className="p-12 text-zinc-500">Ticket not found.</div>;

  return (
    <div className="p-8 lg:p-12 max-w-4xl" data-testid="ticket-detail">
      <Link
        to="/app/tickets"
        className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-6 transition"
        data-testid="back-to-tickets"
      >
        <ArrowLeft className="w-4 h-4" /> Back to tickets
      </Link>

      <div className="bg-white rounded-3xl border border-zinc-200 p-8 lg:p-10">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex-1">
            <div className="font-mono text-xs text-zinc-400 uppercase tracking-widest mb-2">#{ticket.id.slice(0, 8)}</div>
            <h1 className="font-display font-black tracking-tighter text-3xl text-zinc-900">{ticket.title}</h1>
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border ${statusColor[ticket.status]}`}>
            {ticket.status.replace("_", " ")}
          </span>
        </div>

        <div className="flex flex-wrap gap-6 text-sm text-zinc-500 mb-8 pb-8 border-b border-zinc-100">
          <div className="flex items-center gap-2"><Tag className="w-4 h-4" /> {ticket.category}</div>
          <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {ticket.priority} priority</div>
          <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {new Date(ticket.created_at).toLocaleString()}</div>
        </div>

        <h3 className="font-display font-bold text-lg mb-3 text-zinc-900">Description</h3>
        <p className="text-zinc-700 whitespace-pre-wrap leading-relaxed mb-10">{ticket.description}</p>

        {ticket.auto_response && (
          <div className="mb-10 bg-blue-50 border border-blue-200 rounded-2xl p-6 flex gap-4" data-testid="ticket-auto-response">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <Headset className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-sm text-blue-900 mb-1">NextGen Resolution Experts — Automatic Response</div>
              <p className="text-sm text-blue-800 leading-relaxed">{ticket.auto_response}</p>
              <div className="text-xs text-blue-400 mt-2">{new Date(ticket.created_at).toLocaleString()}</div>
            </div>
          </div>
        )}

        <div className="bg-zinc-50 rounded-2xl p-6">
          <div className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-3">Update status</div>
          <div className="flex flex-wrap gap-2">
            {STATUSES.map((s) => (
              <button
                key={s}
                onClick={() => updateStatus(s)}
                disabled={ticket.status === s}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition ${
                  ticket.status === s
                    ? "bg-zinc-900 text-white cursor-default"
                    : "bg-white border border-zinc-200 text-zinc-700 hover:border-zinc-900"
                }`}
                data-testid={`status-${s}`}
              >
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
